import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { geocodeResponseSchema, reverseGeocodeResponseSchema } from '@repo/shared';
import { apiRequest } from '../../lib/api';

/** Long enough to skip most keystrokes, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 250;

/** Below this, results are too broad to be useful. */
const MIN_QUERY_LENGTH = 3;

/** Returns `value` only once it has stopped changing for `delayMs`. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

export interface GeocodeSearchOptions {
  /** `lon,lat` to bias results toward — normally the map's current centre. */
  proximity?: string;
  /** False when no Mapbox token is configured, which disables the query. */
  enabled: boolean;
}

/**
 * Debounced address search. Results are cached per (term, proximity) so
 * backspacing through a query replays instantly instead of re-billing Mapbox.
 */
export function useGeocodeSearch(term: string, { proximity, enabled }: GeocodeSearchOptions) {
  const debouncedTerm = useDebouncedValue(term.trim(), SEARCH_DEBOUNCE_MS);
  const active = enabled && debouncedTerm.length >= MIN_QUERY_LENGTH;

  const query = useQuery({
    queryKey: ['geocode', debouncedTerm, proximity ?? null],
    queryFn: () =>
      apiRequest('/geocode', geocodeResponseSchema, {
        searchParams: { q: debouncedTerm, proximity },
      }),
    enabled: active,
    staleTime: 5 * 60_000,
  });

  return {
    ...query,
    /** True while the user has typed past the threshold but results are pending. */
    isSearching: active && (query.isPending || debouncedTerm !== term.trim()),
    isActive: active,
  };
}

/**
 * Turns coordinates into a place name — used to label the browser's geolocation
 * fix as something friendlier than a decimal pair.
 */
export async function reverseGeocode(longitude: number, latitude: number) {
  const { place } = await apiRequest('/geocode/reverse', reverseGeocodeResponseSchema, {
    searchParams: { longitude, latitude },
  });
  return place;
}
